import React from 'react';

import autoBind from 'react-autobind';
import { If, Then, Else } from 'react-if'
import Dropzone from 'react-dropzone'
import axios from 'axios'

// import other components within the same project here
import Config from "../Config.jsx";

import Localization from "../Localization.jsx"
import GoogleImageSearchDialog from "../views/GoogleImageSearchDialog.jsx"
import AudioRecorder from "./AudioRecorder.jsx"
import ReactAudioPlayer from 'react-audio-player';
import Lightbox from 'react-images';

import { Button, Card, Loader, Text } from '@mantine/core';
import { IconTrash, IconUpload, IconMicrophone, IconFileUpload, IconFileTypePdf } from '@tabler/icons-react';

class FilePicker extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();
    this.cancelSources = [];

    // state variables here
    this.state = {
      // aNumber : 1,
      // aString: "abc",
      // anArray: [],
      // anObject: {},
      fileID: "",
      fileUrl: "",
      previewUrl: "",
      fileDetails: "",
      fileInstruction: "",
      isUploading: false,
      uploadProgress: 0,
      fileTypeIcon: "create_new_folder",
      extensionText: "",
      lightboxIsOpen: false,
      uploadID: "",
      fileIDPendingDelete: "",
      inputMode: "upload", // "upload" | "record" (audio only, when allowRecording)
    };
    autoBind(this);
    if (this.props.allowedExtension) {
      this.state.extensionText = Localization.strings.FILE_TYPE + ": " + this.props.allowedExtension.replace(/,/g, " / ");
      this.state.fileDetails = this.state.extensionText;
    }
    let fileType = this.props.fileType;
    if (fileType == "image") {
      this.state.fileTypeIcon = "add_photo_alternate";
    }
    else if (fileType == "audio") {
      this.state.fileTypeIcon = "audiotrack";
    }
    else if (fileType == "video") {
      this.state.fileTypeIcon = "local_movies";
    }
    if (this.props.fileInstruction) {
      this.state.fileInstruction = this.props.fileInstruction;
    }
    if (this.props.filePath) {
      this.state.fileUrl = this.props.filePath;
    }
    if (this.props.previewPath) {
      this.state.previewUrl = this.props.previewPath;
    }
    if (this.props.uploadID) {
      this.state.uploadID = this.props.uploadID;
    }
    if (this.props.fileID) {
      this.state.fileID = this.props.fileID;
    }

    // Audio-with-recording keeps an independent slot per tab so an uploaded
    // MP3 and a microphone recording can coexist. An already-saved audio
    // (re-editing existing content) loads into the tab it was created with so
    // its audio and voice-to-text reappear there. Content saved without a
    // recorded source (legacy, or uploaded) defaults to the Upload File tab.
    this.state.uploadSlot = { fileID: "", fileUrl: "", previewUrl: "", res: null };
    this.state.recordSlot = { fileID: "", fileUrl: "", previewUrl: "", res: null };
    if (this.props.allowRecording && this.props.fileType == "audio" && (this.props.fileID || this.props.filePath)) {
      let loadedSlot = {
        fileID: this.props.fileID || "",
        fileUrl: this.props.filePath || "",
        previewUrl: this.props.previewPath || "",
        res: this.props.audioFileInfo || (this.props.fileID ? { file_id: this.props.fileID, path: "" } : null),
      };
      if (this.props.audioSource == "record") {
        this.state.recordSlot = loadedSlot;
        this.state.inputMode = "record";
      } else {
        this.state.uploadSlot = loadedSlot;
        this.state.inputMode = "upload";
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Audio-with-recording drives its display from the per-tab slots, so it
    // must NOT re-sync the flat file fields from props: payload.sound0 mirrors
    // the active tab and would otherwise clobber the inactive tab on a switch.
    let canRecord = this.props.allowRecording && this.props.fileType == "audio";
    // Update state when relevant props change
    if (!canRecord && prevProps.fileID !== this.props.fileID) {
      this.setState({ fileID: this.props.fileID || "" });
      this.state.fileID = this.props.fileID || "";
    }
    if (!canRecord && prevProps.filePath !== this.props.filePath) {
      this.setState({ fileUrl: this.props.filePath || "" });
      this.state.fileUrl = this.props.filePath || "";
    }
    if (!canRecord && prevProps.previewPath !== this.props.previewPath) {
      this.setState({ previewUrl: this.props.previewPath || "" });
      this.state.previewUrl = this.props.previewPath || "";
    }
    if (prevProps.uploadID !== this.props.uploadID) {
      this.setState({ uploadID: this.props.uploadID || "" });
      this.state.uploadID = this.props.uploadID || "";
    }
  }

  componentWillUnmount() {
    this.cancelAllRequests();
  }

  createCancelSource() {
    const source = axios.CancelToken.source();
    this.cancelSources.push(source);
    return source;
  }

  releaseCancelSource(source) {
    this.cancelSources = this.cancelSources.filter((item) => item !== source);
  }

  cancelAllRequests() {
    this.cancelSources.forEach((source) => {
      try {
        source.cancel('Upload cancelled');
      } catch (error) {
        console.log(error);
      }
    });
    this.cancelSources = [];
  }

  getFileExtension(filename) {
    return (/[.]/.exec(filename)) ? /[^.]+$/.exec(filename)[0] : undefined;
  }

  getBaseFileName(filename) {
    const extension = this.getFileExtension(filename);
    if (!extension) {
      return filename;
    }
    return filename.slice(0, -(extension.length + 1));
  }

  getMimeType(filename, fallbackType = "") {
    if (fallbackType && fallbackType !== "") {
      return fallbackType;
    }

    const extension = (this.getFileExtension(filename) || "").toLowerCase();
    const mimeMap = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      webp: "image/webp",
      bmp: "image/bmp",
      mp3: "audio/mpeg",
      m4a: "audio/mp4",
      wav: "audio/wav",
      mp4: "video/mp4",
      m4v: "video/mp4",
      glb: "model/gltf-binary",
      json: "application/json",
      pdf: "application/pdf",
      txt: "text/plain",
    };

    return mimeMap[extension] || "application/octet-stream";
  }

  shouldGenerateThumbnail(file) {
    return this.props.fileType === "image" && file != null;
  }

  buildThumbnailFileName(filename, extension) {
    return this.getBaseFileName(filename) + "_thumb." + extension;
  }

  checkFileExtension(filename, allowedExtension) {
    let fileOK = false;

    if (allowedExtension == undefined || allowedExtension == "") {
      return true;
    }

    filename = filename.toLowerCase();
    allowedExtension = allowedExtension.toLowerCase();

    let extension = this.getFileExtension(filename);
    if (extension == undefined) {
      return false;
    }

    extension = extension.trim();
    let extensionArray = allowedExtension.split(',');
    for (let i = 0; i < extensionArray.length; i++) {
      if (extension == extensionArray[i].trim()) {
        fileOK = true;
      }
    }

    return fileOK;
  }

  checkFileSize(fileSize, sizeLimit) {
    return fileSize <= sizeLimit;
  }

  handleChangeClicked() {
    let dialogText = {
      title: Localization.strings.DELETE,
      content: Localization.strings.DELETE_REMINDER,
      confirm: Localization.strings.DELETE,
    };
    console.log("on delete file clicked"); 
    this.props.root.refs.confirmDialog.show(dialogText, this.changeFileConfirmClicked, this.changeFileCancelClicked);
  }

  changeFileConfirmClicked() {
    this.setState({
      fileUrl: "",
      previewUrl: "",
      fileDetails: this.state.extensionText,
    });
    this.state.fileUrl = "";
    this.state.previewUrl = "";
    this.state.fileDetails = this.state.extensionText;

    if (this.state.fileID != "") {
      this.setState({
        fileIDPendingDelete: this.state.fileID,
      });
      this.state.fileIDPendingDelete = this.state.fileID;
    }

    // reset payload
    try{
      this.props.onDeleteFile(this.state.fileID);
    }catch(error){
      console.log(error);
    }
  }

  deleteFile() {
    if (this.state.fileIDPendingDelete != "") {
      let requestObj = { file_id: this.state.fileIDPendingDelete };
      this.props.root.refs.API.request("delete_file", requestObj, this.deleteFileOK, this.deleteFileFailed);
    }
  }

  deleteFileOK(res) {
    console.log("delete file ok");
    this.setState({
      fileIDPendingDelete: "",
    });
    this.state.fileIDPendingDelete = "";
  }

  deleteFileFailed(err) {
    console.log("delete file failed " + err);
  }

  changeFileCancelClicked() {

  }

  // Per-tab delete for the audio picker: removes only the active tab's audio
  // (the other tab's recording/upload is left intact).
  deleteActiveAudio() {
    let dialogText = {
      title: Localization.strings.DELETE,
      content: Localization.strings.DELETE_REMINDER,
      confirm: Localization.strings.DELETE,
    };
    this.props.root.refs.confirmDialog.show(dialogText, this.confirmDeleteActiveAudio, this.changeFileCancelClicked);
  }

  confirmDeleteActiveAudio() {
    let isRecordTab = this.state.inputMode == "record";
    let slotKey = isRecordTab ? "recordSlot" : "uploadSlot";
    let removed = this.state[slotKey];
    let emptySlot = { fileID: "", fileUrl: "", previewUrl: "", res: null };

    this.setState({
      [slotKey]: emptySlot,
      fileID: "",
      fileUrl: "",
      previewUrl: "",
      fileDetails: this.state.extensionText,
    });
    this.state[slotKey] = emptySlot;
    this.state.fileID = "";
    this.state.fileUrl = "";
    this.state.previewUrl = "";
    this.state.fileDetails = this.state.extensionText;

    if (removed && removed.fileID) {
      this.setState({ fileIDPendingDelete: removed.fileID });
      this.state.fileIDPendingDelete = removed.fileID;
      this.deleteFile();
    }

    if (this.props.onActiveAudioChange) {
      this.props.onActiveAudioChange(null);
    }
  }

  abortUpload(reason, suppressNotification) {
    this.setState({ isUploading: false });
    this.state.isUploading = false;
    this.resetUploadProgress();
    if (this.props.onAbortUpload != null) {
      this.props.onAbortUpload();
    }
    if (suppressNotification) {
      return;
    }
    let message = Localization.strings.UPLOAD_FAILED;
    if (reason) {
      message = message + " (" + reason + ")";
    }
    this.props.root.addNotification(message, false);
  }

  resetUploadProgress() {
    this.setState({ uploadProgress: 0 });
    this.state.uploadProgress = 0;
  }

  imageSearchClicked() {
    this.refs.googleImageSearchDialog.setState({ open: true });
  }

  imagePreviewClicked() {
    this.setState({ lightboxIsOpen: true });
    this.state.lightboxIsOpen = true;
  }

  imageFullClicked() {
    this.setState({ lightboxIsOpen: false });
    this.state.lightboxIsOpen = false;
  }

  handleUploadProgress(progressEvent) {
    var percentCompleted = Math.round( (progressEvent.loaded * 100) / progressEvent.total );

    this.setState({ isUploading: true });
    this.state.isUploading = true;
    this.setState({ uploadProgress: percentCompleted });
    this.state.uploadProgress = percentCompleted;
  }

  handleUploadResponse(response) {
    if (response.status != 200) {
      return;
    }

    let resp = response.data;
    resp.uploadID = this.state.uploadID;

    let fileUrl = resp.path ? Config.UPLOAD_PATH() + resp.path : "";
    let previewPath = resp.tn_path ? resp.tn_path : resp.path;
    let previewUrl = previewPath ? Config.UPLOAD_PATH() + previewPath : fileUrl;

    let canRecord = this.props.allowRecording && this.props.fileType == "audio";
    if (canRecord) {
      // Route the finished upload into whichever tab started it and make that
      // tab the active (valid) audio. The other tab's slot is left untouched.
      let isRecordTab = this.state.inputMode == "record";
      let slotKey = isRecordTab ? "recordSlot" : "uploadSlot";
      let previousFileID = this.state[slotKey] ? this.state[slotKey].fileID : "";
      let newSlot = { fileID: resp.file_id || "", fileUrl: fileUrl, previewUrl: previewUrl, res: resp };

      this.setState({
        [slotKey]: newSlot,
        uploadProgress: 100,
        isUploading: false,
        fileID: resp.file_id || "",
        fileUrl: fileUrl,
        previewUrl: previewUrl,
        fileIDPendingDelete: previousFileID || this.state.fileIDPendingDelete,
      });
      this.state[slotKey] = newSlot;
      this.state.uploadProgress = 100;
      this.state.isUploading = false;
      this.state.fileID = resp.file_id || "";
      this.state.fileUrl = fileUrl;
      this.state.previewUrl = previewUrl;
      if (previousFileID) {
        this.state.fileIDPendingDelete = previousFileID;
      }

      if (this.props.onActiveAudioChange) {
        this.props.onActiveAudioChange(resp, isRecordTab ? "record" : "upload");
      }
      this.props.root.addNotification(Localization.strings.UPLOADED, true);
      this.deleteFile();
      return;
    }

    if (this.props.onUploadSuccess != null) {
      this.props.onUploadSuccess(resp);
      this.setState({
        uploadProgress: 100,
        isUploading: false,
        fileUrl: fileUrl,
        previewUrl: previewUrl,
      });
      this.state.uploadProgress = 100;
      this.state.isUploading = false;
      this.state.fileUrl = fileUrl;
      this.state.previewUrl = previewUrl;
      this.props.root.addNotification(Localization.strings.UPLOADED, true);
      this.deleteFile();
    }
  }

  async loadImageFile(file) {
    return new Promise((resolve, reject) => {
      const objectUrl = window.URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        window.URL.revokeObjectURL(objectUrl);
        resolve(img);
      };
      img.onerror = () => {
        window.URL.revokeObjectURL(objectUrl);
        reject(new Error("Failed to load image for thumbnail generation."));
      };
      img.src = objectUrl;
    });
  }

  async generateThumbnail(file, maxWidth = 640, maxHeight = 480) {
    const image = await this.loadImageFile(file);
    const widthScale = maxWidth / image.width;
    const heightScale = maxHeight / image.height;
    const scale = Math.min(1, widthScale, heightScale);
    const targetWidth = Math.max(1, Math.round(image.width * scale));
    const targetHeight = Math.max(1, Math.round(image.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas is not available for thumbnail generation.");
    }

    const sourceMimeType = (file.type || "").toLowerCase();
    const usePng = sourceMimeType.includes("png")
      || sourceMimeType.includes("webp")
      || sourceMimeType.includes("gif")
      || sourceMimeType.includes("bmp");
    const outputType = usePng ? "image/png" : "image/jpeg";
    const outputExtension = usePng ? "png" : "jpg";

    if (outputType === "image/jpeg") {
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, targetWidth, targetHeight);
    }
    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((thumbnailBlob) => {
        if (!thumbnailBlob) {
          reject(new Error("Failed to create thumbnail blob."));
          return;
        }
        resolve(thumbnailBlob);
      }, outputType, outputType === "image/jpeg" ? 0.9 : undefined);
    });

    return {
      blob,
      extension: outputExtension,
      mimeType: outputType,
      fileName: this.buildThumbnailFileName(file.name, outputExtension),
    };
  }

  /**
   * The two blob-upload cmds (request_upload / confirm_upload) used to bypass
   * API.jsx and POST the legacy {cmd, json} form directly. They now go through
   * the same cmd->route adapter as everything else, so they carry the bearer
   * token and hit POST /api/upload/request and POST /api/upload/confirm.
   *
   * The contract this function exposes to its callers is unchanged: it resolves
   * with `body.result` and THROWS on failure (the same negative-number rule
   * API.jsx applies), so uploadFile's try/catch still drives the UI.
   */
  async postAPIRequest(cmd, requestObj = {}) {
    const api = (this.props.root && this.props.root.refs) ? this.props.root.refs.API : null;
    if (!api) {
      throw new Error("API not available.");
    }

    const body = await new Promise((resolve, reject) => {
      api.request(
        cmd,
        requestObj,
        (res) => resolve(res && res.body),
        (err) => {
          const remarks = (err && err.body && err.body.remarks) ? err.body.remarks : "";
          reject(new Error(remarks || "API request failed."));
        },
      );
    });

    if (!body || typeof body !== "object") {
      throw new Error("Empty API response.");
    }

    if (typeof body.result === "number" && body.result < 0) {
      throw new Error(body.message || "API request failed.");
    }

    return body.result;
  }

  async uploadBlobToAzure({ sasUrl, data, contentType, onProgress = null }) {
    const source = this.createCancelSource();
    try {
      const response = await axios.put(
        sasUrl,
        data,
        {
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": contentType,
          },
          onUploadProgress: onProgress,
          cancelToken: source.token,
        }
      );

      if (response.status < 200 || response.status >= 300) {
        throw new Error("Blob upload failed.");
      }

      return response;
    } finally {
      this.releaseCancelSource(source);
    }
  }

  buildLegacyUploadResponse(file, confirmedFile) {
    const extension = (this.getFileExtension(file.name) || "").toLowerCase();
    const displayName = confirmedFile.display_name || this.getBaseFileName(file.name);
    const fileType = confirmedFile.file_type || extension;
    const fileSize = confirmedFile.file_size != null ? confirmedFile.file_size : parseInt(file.size);

    return {
      ok: confirmedFile.ok != null ? confirmedFile.ok : 1,
      file_id: confirmedFile.file_id,
      display_name: displayName,
      path: confirmedFile.path || "",
      tn_path: confirmedFile.tn_path || "",
      file_type: fileType,
      file_size: fileSize,
      status: confirmedFile.status || "active",
      message: confirmedFile.message || (file.name + " uploaded successfully."),
    };
  }

  async uploadFile(files, progressCallback, responseCallback) {
    if (files.length > 0) {
      let oneMB = 1024 * 1024;
      let file = files[0];
      let fileOK = false;
      let fileSize = parseInt(file.size);
      fileOK = this.checkFileExtension(file['name'], this.props.allowedExtension? this.props.allowedExtension : "");
      if (!fileOK) {
        this.props.root.addNotification(Localization.strings.UPLOAD_FILE_TYPE_REMINDER, false);
      }
      if (fileOK) {
        let sizeLimit = (this.props.sizeLimit? this.props.sizeLimit : oneMB);
        fileOK = this.checkFileSize(fileSize, sizeLimit);
        if (!fileOK) {
          let message = Localization.strings.UPLOAD_FILE_SIZE_REMINDER + " " + parseFloat(sizeLimit / oneMB).toFixed(1) + "MB";
          this.props.root.addNotification(message, false);
        }
      }

      if (fileOK) {
        try {
          this.setState({ isUploading: true, uploadProgress: 0 });
          this.state.isUploading = true;
          this.state.uploadProgress = 0;

          const shouldGenerateThumbnail = this.shouldGenerateThumbnail(file);
          const thumbnail = shouldGenerateThumbnail ? await this.generateThumbnail(file) : null;
          const totalBytes = file.size + (thumbnail ? thumbnail.blob.size : 0);
          const uploadProgressState = {
            original: 0,
            thumbnail: 0,
          };
          const updateCombinedProgress = () => {
            progressCallback({
              loaded: uploadProgressState.original + uploadProgressState.thumbnail,
              total: Math.max(totalBytes, 1),
            });
          };

          const requestFiles = [{
            filename: file.name,
            extension: this.getFileExtension(file.name),
            file_size: file.size,
            needs_thumbnail: shouldGenerateThumbnail ? 1 : 0,
            thumbnail_extension: thumbnail ? thumbnail.extension : "",
            thumbnail_file_size: thumbnail ? thumbnail.blob.size : 0,
          }];

          const requestResult = await this.postAPIRequest("request_upload", {
            user_id: this.props.root.getMyUserId(),
            epack_id: (this.props.epackID ? this.props.epackID : 0),
            files: requestFiles,
          });

          const registration = requestResult && Array.isArray(requestResult.files)
            ? requestResult.files[0]
            : null;

          if (!registration || registration.error) {
            throw new Error(registration && registration.error ? registration.error : "request_upload failed.");
          }

          updateCombinedProgress();
          await this.uploadBlobToAzure({
            sasUrl: registration.sas_url,
            data: file,
            contentType: this.getMimeType(file.name, file.type),
            onProgress: (progressEvent) => {
              uploadProgressState.original = progressEvent && progressEvent.loaded ? progressEvent.loaded : 0;
              updateCombinedProgress();
            },
          });
          uploadProgressState.original = file.size;
          updateCombinedProgress();

          if (thumbnail) {
            if (!registration.tn_sas_url) {
              throw new Error("Thumbnail SAS URL missing.");
            }

            await this.uploadBlobToAzure({
              sasUrl: registration.tn_sas_url,
              data: thumbnail.blob,
              contentType: thumbnail.mimeType,
              onProgress: (progressEvent) => {
                uploadProgressState.thumbnail = progressEvent && progressEvent.loaded ? progressEvent.loaded : 0;
                updateCombinedProgress();
              },
            });
            uploadProgressState.thumbnail = thumbnail.blob.size;
            updateCombinedProgress();
          }

          const confirmResult = await this.postAPIRequest("confirm_upload", {
            file_ids: [registration.file_id],
          });
          const confirmedFile = confirmResult && Array.isArray(confirmResult.files)
            ? confirmResult.files[0]
            : confirmResult;

          if (!confirmedFile || confirmedFile.error) {
            throw new Error(confirmedFile && confirmedFile.error ? confirmedFile.error : "confirm_upload failed.");
          }

          responseCallback({
            status: 200,
            data: this.buildLegacyUploadResponse(file, confirmedFile),
          });
        } catch (error) {
          if (axios.isCancel(error)) {
            console.log("upload cancelled");
            return;
          }
          console.log(error);
          let reason = "";
          if (error) {
            if (error.response && error.response.status) {
              reason = "HTTP " + error.response.status;
            } else if (error.message) {
              reason = error.message;
            }
          }
          this.abortUpload(reason);
        }
      }
    }
  }

  cancelUpload() {
    this.cancelAllRequests();
    this.abortUpload(undefined, true);
  }

  setInputMode(mode) {
    this.setState({ inputMode: mode });
    this.state.inputMode = mode;

    let canRecord = this.props.allowRecording && this.props.fileType == "audio";
    if (!canRecord) {
      return;
    }

    // The newly-active tab's audio is the only valid one: mirror it into the
    // single saved slot (payload.sound0) so Finish keeps exactly this tab's
    // audio, and align the flat display fields with it.
    let slot = mode == "record" ? this.state.recordSlot : this.state.uploadSlot;
    let hasFile = !!(slot && slot.fileID);
    this.setState({
      fileID: hasFile ? slot.fileID : "",
      fileUrl: hasFile ? slot.fileUrl : "",
      previewUrl: hasFile ? slot.previewUrl : "",
    });
    this.state.fileID = hasFile ? slot.fileID : "";
    this.state.fileUrl = hasFile ? slot.fileUrl : "";
    this.state.previewUrl = hasFile ? slot.previewUrl : "";

    if (this.props.onActiveAudioChange) {
      this.props.onActiveAudioChange(hasFile ? slot.res : null, mode);
    }
  }

  // Called by AudioRecorder once the user confirms a microphone recording.
  // The recorder hands back a real .mp3 File, so it can reuse the exact same
  // upload pipeline as a file picked from disk.
  onAudioRecorded(file) {
    this.uploadFile([file], this.handleUploadProgress, this.handleUploadResponse);
  }

  renderUploadDropzone() {
    return (
      <div style={{width:'100%', height:'100%'}}>
        <Dropzone onDrop={acceptedFiles => this.uploadFile(acceptedFiles, this.handleUploadProgress, this.handleUploadResponse)}>
          {({getRootProps, getInputProps}) => (
              <div style={{width:'100%', height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'10px'}} {...getRootProps()}>
                <input {...getInputProps()} />
                <IconFileUpload size={44} stroke={1.5} color="#868e96" />
                <p style={{fontWeight:'bold', margin:0, textAlign:'center'}}>{Localization.strings.DROP_FILE_HERE_OR_CLICK_TO_SELECT}</p>
              </div>
          )}
        </Dropzone>
      </div>
    );
  }

  renderUploadingSpinner() {
    return (
      <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center'}}>
        <div style={{textAlign:'center', verticalAlign:'middle'}}>
          <Loader style={{margin:'0 auto'}} />
          <br/>
          {Localization.strings.UPLOADING + "..." + parseInt(this.state.uploadProgress) + "%"}
          <Button variant="subtle" onClick={this.cancelUpload}>{Localization.strings.CANCEL}</Button>
        </div>
      </div>
    );
  }

  // Preview for an already-uploaded file. Video renders in a real <video>
  // element: an .mp4 in the <img> branch used to draw as a broken image, and
  // preload="metadata" makes the browser paint the first frame as a poster for
  // free (which is why video uploads do not generate a thumbnail blob).
  renderFilePreview() {
    if (this.props.fileType == "audio") {
      return (
        <ReactAudioPlayer
          src={this.state.fileUrl}
          controls
          style={{display:"block", margin:"auto"}}
        />
      );
    }

    if (this.props.fileType == "video") {
      // Fills the 16:9 stage render() sets up for video; object-fit keeps a
      // clip of any aspect ratio letterboxed inside it rather than stretched.
      return (
        <video
          src={this.state.fileUrl}
          controls
          preload="metadata"
          style={{width:"100%", height:"100%", objectFit:"contain", display:"block", backgroundColor:"#000"}}
        />
      );
    }

    // A document (today: the Extra Locale 教師參考手冊 PDF) has no visual
    // preview worth rendering inline — an <img> pointed at a PDF just draws a
    // broken-image glyph, which is what this branch exists to stop. Show what
    // the file IS and give a way to open it; the caller's own row is where a
    // download with a proper filename lives.
    if (this.props.fileType == "document") {
      let documentName = this.props.fileDisplayName || Localization.strings.UPLOADED_FILE;
      return (
        <div style={{width:'100%', padding:'0 16px', textAlign:'center'}}>
          <IconFileTypePdf size={44} stroke={1.5} color="#868e96" />
          <Text size="sm" fw={600} style={{wordBreak:'break-all', marginTop:4}}>
            {documentName}
          </Text>
          <Button
            variant="subtle"
            size="xs"
            component="a"
            href={this.state.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            mt={4}
          >
            {Localization.strings.OPEN_FILE}
          </Button>
        </div>
      );
    }

    return (
      <img
        alt=""
        style={{cursor: "pointer", height:"20vh", maxHeight:"100%", maxWidth:"100%", display:"block", margin:"auto"}}
        src={this.state.previewUrl || this.state.fileUrl}
        onClick={this.imagePreviewClicked}
      />
    );
  }

  renderAudioSlot(slotKey) {
    let slot = slotKey == "record" ? this.state.recordSlot : this.state.uploadSlot;
    return (
      <div style={{width:'100%', textAlign:'center'}}>
        <ReactAudioPlayer
          src={slot.fileUrl}
          controls
          style={{display:'block', margin:'0 auto 8px auto'}}
        />
        <Button leftSection={<IconTrash size={16} />} onClick={this.deleteActiveAudio}>
          {Localization.strings.DELETE}
        </Button>
      </div>
    );
  }

  renderInputArea() {
    let canRecord = this.props.allowRecording && this.props.fileType == "audio";
    if (!canRecord) {
      return this.renderUploadDropzone();
    }

    let isUpload = this.state.inputMode != "record";
    return (
      <div style={{width:'100%', height:'100%', display:'flex', flexDirection:'column'}}>
        <div style={{display:'flex', borderBottom:`1px solid #e0e0e0`}}>
          <Button
            fullWidth
            size="sm"
            variant={isUpload ? "filled" : "subtle"}
            style={{borderRadius:0}}
            leftSection={<IconUpload size={16} />}
            onClick={() => this.setInputMode("upload")}
          >
            {Localization.strings.UPLOAD_FILE}
          </Button>
          <Button
            fullWidth
            size="sm"
            variant={!isUpload ? "filled" : "subtle"}
            style={{borderRadius:0}}
            leftSection={<IconMicrophone size={16} />}
            onClick={() => this.setInputMode("record")}
          >
            {Localization.strings.RECORD_AUDIO}
          </Button>
        </div>
        <div style={{flex:1, minHeight:0, display:'flex', alignItems:'center', justifyContent:'center'}}>
          {/*
            Both tabs stay mounted and are toggled with CSS so each tab keeps
            its own audio (an uploaded MP3 and a recording can coexist) and an
            in-progress recording survives switching tabs. Only the active tab's
            audio is saved (see setInputMode / onActiveAudioChange).
          */}
          <div style={{width:'100%', height:'100%', display: isUpload ? 'flex' : 'none', alignItems:'center', justifyContent:'center'}}>
            {this.state.isUploading && isUpload
              ? this.renderUploadingSpinner()
              : (this.state.uploadSlot.fileUrl != ""
                  ? this.renderAudioSlot("upload")
                  : this.renderUploadDropzone())}
          </div>
          <div style={{width:'100%', height:'100%', display: isUpload ? 'none' : 'flex', alignItems:'center', justifyContent:'center'}}>
            {this.state.isUploading && !isUpload
              ? this.renderUploadingSpinner()
              : (this.state.recordSlot.fileUrl != ""
                  ? this.renderAudioSlot("record")
                  : (
                    <AudioRecorder
                      root={this.props.root}
                      onRecorded={this.onAudioRecorded}
                    />
                  ))}
          </div>
        </div>
      </div>
    );
  }

  render() {
    let canRecord = this.props.allowRecording && this.props.fileType == "audio";
    let isVideo = this.props.fileType == "video";
    // A caller that already draws its own titled Card (the movie clip's tabbed
    // source picker) passes `bare`, so the picker doesn't nest a second border
    // and a second inset inside it.
    let bare = !!this.props.bare;
    // Video is the one preview that has a meaningful shape: the fixed 20vh
    // thumbnail strip squeezed a 16:9 clip into a letterboxed sliver. The black
    // matte belongs to the PLAYER only — using it for the empty state too made
    // the dropzone's dark label invisible against it.
    let showingPreview = !this.state.isUploading && this.state.fileUrl != "";
    let stageStyle = isVideo
      ? {
          backgroundColor: showingPreview ? '#000' : '#f5f5f5',
          width: '100%',
          aspectRatio: '16 / 9',
          maxHeight: '46vh',
        }
      : {backgroundColor:'#f5f5f5', height:'20vh'};

    let body = (
      <>
        <If condition={this.props.pickerTitle != null && this.props.pickerTitle != ""}>
          <Then>
            <Card.Section inheritPadding py="sm" px="md">
              {/* component="div": titles may be nodes (e.g. text + info icon),
                  and block content inside the default <p> is invalid HTML. */}
              <Text fw={600} component="div">{this.props.pickerTitle}</Text>
            </Card.Section>
          </Then>
        </If>
        <Card.Section>
          <div style={stageStyle}>
            <If condition={canRecord}>
              <Then>
                {this.renderInputArea()}
              </Then>
              <Else>
                <If condition={this.state.isUploading}>
                  <Then>
                    {this.renderUploadingSpinner()}
                  </Then>
                  <Else>
                    <If condition={this.state.fileUrl == ""}>
                      <Then>
                        {this.renderInputArea()}
                      </Then>
                      <Else>
                        <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center'}}>
                          {this.renderFilePreview()}
                        </div>
                      </Else>
                    </If>
                  </Else>
                </If>
              </Else>
            </If>
          </div>
        </Card.Section>
        <div style={{padding:16}}>
          {/* File meta and its Delete action used to run together inline, which
              collided at narrow widths. Own row, own gap, wraps cleanly. */}
          <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:12, flexWrap:'wrap'}}>
            <If condition={this.state.fileDetails != ""}>
              <Then>
                <Text size="sm" c="dimmed">{this.state.fileDetails}</Text>
              </Then>
            </If>
            <If condition={!canRecord && this.state.fileUrl != ""}>
              <Then>
                <Button size="xs" leftSection={<IconTrash size={16} />} onClick={this.handleChangeClicked}>
                  {Localization.strings.DELETE}
                </Button>
              </Then>
            </If>
          </div>
          <If condition={this.state.fileInstruction != ""}>
            <Then>
              <Text size="xs" c="dimmed" style={{marginTop:10, textAlign:'center', lineHeight:1.6}}>
                {this.state.fileInstruction.split('\n').map((item, key) => {
                  return (
                    <span key={key}>
                      {item}
                      <br/>
                    </span>
                  )
                })}
              </Text>
            </Then>
          </If>
        </div>
        {this.props.extraContent ? this.props.extraContent : null}
      </>
    );

    return (
      <div style={bare ? undefined : {margin:"0 5px 10px 5px"}}>
        <div style={{display:'none'}}>
          <GoogleImageSearchDialog root={this.props.root} parent={this} ref="googleImageSearchDialog" />
        </div>
        <If condition={bare}>
          <Then>
            <Card padding={0} withBorder={false} style={{backgroundColor:'transparent'}}>
              {body}
            </Card>
          </Then>
          <Else>
            <Card withBorder padding={0}>
              {body}
            </Card>
          </Else>
        </If>
        <Lightbox
          images={[{src:(this.state.fileUrl || this.state.previewUrl)}]}
          isOpen={this.state.lightboxIsOpen}
          onClickImage={this.imageFullClicked}
          onClose={this.imageFullClicked}
          backdropClosesModal={true}
          showImageCount={false}
          theme={{
            // Ensure the preview lightbox sits above any MUI Dialogs
            container: { zIndex: 120002 },
            overlay: { zIndex: 120001 },
            close: { zIndex: 120003 }
          }}
        />
      </div>
    )
  }
}

export default FilePicker;
